import { useState, useEffect, useRef, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { CHEVRON_DOWN, REMOVETEXT } from 'constants/images';
import { CheckButton } from 'components/buttons';
import { useClickOutside } from 'hooks';
import { useInfiniteQuery, useQuery } from 'react-query';
import { axiosPrivate, customerAPI } from 'api';
import { useUserInfoContext } from 'context';
import { debounce } from 'lodash';
import './index.scss';
import { clearReportsParameters } from 'utils/userStorage';


const transformOption = (data) => {
  const categories = data.pages[0].data.categories.map((v) => ({
    id: +v.id,
    value: v.name,
  }));
  const items = categories.reduce((acc, cur) => {
    acc[cur.id] = data.pages
      .flatMap(
        (page) => page.data.categories.filter((category) => category.id === cur.id)[0].products
      )
      .map((item) => ({
        id: +item.id,
        value: item.name,
      }));
    return acc;
  }, {});
  return {
    categories,
    items,
    combination: categories.map((category) => ({
      id: category.id,
      value: category.value,
      products: items[category.id],
    })),
  };
};

const OptionsDropdown = ({
  isFocused,
  isSearchRequestEnabled,
  queryKey,
  parameters,
  setParametersState,
  reportsParameters,
  clickedCategory,
  clickedAnyMoment,
  handleCategoryClick,
  handleAnyMomentClick,
  handleClickItemValueAll,
  handleSelectValue,
  anyMomentOptions,
  searchedValues,
  inputValue,
}) => {
  const { t } = useTranslation();
  const itemListRef = useRef(null);
  const [isParametersSetFinished, setIsParametersSetFinished] = useState(false);
  const { userInfo } = useUserInfoContext();
  const [target, setTarget] = useState(null);

  const { data: optionWithCategoryAndItem, fetchNextPage: fetchNextItemList } = useInfiniteQuery(
    [queryKey, 'option-category', userInfo?.company.shopId],
    async ({ pageParam = 1 }) => {
      const { data } = await axiosPrivate.post(customerAPI.OPTION_CATEGORY, {
        company_id: userInfo?.company.companyId,
        shop_id: userInfo?.company.shopId,
        page: pageParam,
      });
      return { data, nextPage: pageParam + 1 };
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      select: (data) => transformOption(data),
    }
  );
  // set the initial parameters right after fetching the list
  useEffect(() => {
    if (optionWithCategoryAndItem && !isParametersSetFinished) {
      if (reportsParameters) {
        let newCategory = null
        let defaultCategory = null
        optionWithCategoryAndItem.categories.forEach((category) => {
          if (category.id === 0) {
            defaultCategory = category
          } else if (category.id === reportsParameters.category) {
            newCategory = category
          }
        })
        newCategory = newCategory || defaultCategory

        let newItem = null
        let defaultItem = null
        optionWithCategoryAndItem.items[newCategory.id].forEach((item) => {
          if (item.id === 0) {
            newItem = item
          } else if (item.id === reportsParameters.item) {
            defaultItem = item
          }
        })
        newItem = newItem || defaultItem

        setParametersState((prev) => ({
          ...prev,
          category: newCategory,
          item: newItem,
        }));
        clearReportsParameters();
      } else {
        setParametersState((prev) => ({
          ...prev,
          category: {
            id: optionWithCategoryAndItem.combination[0].id,
            value: optionWithCategoryAndItem.combination[0].value,
          },
          item: {
            id: optionWithCategoryAndItem.combination[0].products[0].id,
            value: optionWithCategoryAndItem.combination[0].products[0].value,
          },
        }));
      }

      setIsParametersSetFinished(true);
    }
  }, [optionWithCategoryAndItem]);

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) fetchNextItemList();
      },
      {
        root: itemListRef.current,
      }
    );

    target && intersectionObserver.observe(target);

    return () => {
      intersectionObserver.disconnect();
    };
  }, [target]);

  const searchedList = searchedValues.map((searched, i) => {
    const lengthOfInputValue = inputValue.length;

    const indexOfCategory = searched.category.value.toUpperCase().indexOf(inputValue.toUpperCase());
    const indexOfItem = searched.item.value.toUpperCase().indexOf(inputValue.toUpperCase());

    let remainInputValueCharacterCountInCategory = lengthOfInputValue;
    let remainInputValueCharacterCountInItem = lengthOfInputValue;

    const categoryValue =
      indexOfCategory === -1 ? (
        <span>{searched.category.value}</span>
      ) : (
        searched.category.value.split('').map((character, i) => {
          if (
            (indexOfCategory === i || indexOfCategory < i) &&
            remainInputValueCharacterCountInCategory !== 0
          ) {
            remainInputValueCharacterCountInCategory -= 1;
            return (
              <span className="selected" key={`${character}${i}`}>
                {character}
              </span>
            );
          }
          return <span key={`${character}${i}`}>{character}</span>;
        })
      );
    const itemValue =
      indexOfItem === -1 ? (
        <span>{searched.item.value}</span>
      ) : (
        searched.item.value.split('').map((character, i) => {
          if (
            (indexOfItem === i || indexOfItem < i) &&
            remainInputValueCharacterCountInItem !== 0
          ) {
            remainInputValueCharacterCountInItem -= 1;
            return (
              <span className="selected" key={`${character}${i}`}>
                {character}
              </span>
            );
          }
          return <span key={`${character}${i}`}>{character}</span>;
        })
      );

    return (
      <li
        key={`a${i}`}
        className={`purchased-item_options-list-item`}
        onClick={() => {
          if (searched.item.id === 0) {
            handleClickItemValueAll(searched.category);
            return;
          }
          handleSelectValue(searched.category, searched.item, clickedAnyMoment);
        }}
      >
        {categoryValue}
        &nbsp;&gt;&nbsp;
        {itemValue}
      </li>
    );
  });

  return (
    (<div className={`purchased-item_options${isFocused ? ' open' : ''}`}>
      {!optionWithCategoryAndItem ? (
        <></>
      ) : isSearchRequestEnabled ? (
        <div className="purchased-item_options-search">
          <div className="purchased-item_options-title">Category &gt; Item</div>
          <ul className="purchased-item_options-list">{searchedList}</ul>
        </div>
      ) : (
        <>
          <div className="purchased-item_options-left">
            <div className="purchased-item_options-title">{t("retention.category")}</div>
            <ul className="purchased-item_options-list">
              {optionWithCategoryAndItem.categories.map((option, i) => (
                <li
                  key={`${option.id}${option.value}${i}`}
                  className={`purchased-item_options-list-item${
                    option.id === clickedCategory.id ? ' selected' : ''
                  }`}
                  onClick={() => {
                    handleCategoryClick(option);
                    itemListRef.current.scrollTop = 0;
                  }}
                >
                  {option.value}
                </li>
              ))}
            </ul>
          </div>
          <div className="purchased-item_options-right">
            <div className="purchased-item_options-title">{t("overview.item")}</div>
            <ul className="purchased-item_options-list" ref={itemListRef}>
              {clickedCategory.value ? (
                optionWithCategoryAndItem.items[clickedCategory.id]?.map((option, i) => (
                  <li
                    className={`purchased-item_options-list-item${
                      option.id === parameters.item.id ? ' selected' : ''
                    }`}
                    key={`${option.id}${option.value}${i}`}
                    onClick={() => {
                      handleSelectValue(clickedCategory, option, clickedAnyMoment);
                    }}
                    ref={
                      i === optionWithCategoryAndItem.items[clickedCategory.id].length - 100
                        ? setTarget
                        : null
                    }
                  >
                    {option.value}
                  </li>
                ))
              ) : (
                <></>
              )}
            </ul>
          </div>
        </>
      )}
      <div className="purchased-item_options-bottom">
        {clickedAnyMoment && (
          <CheckButton
            label={t("retention.v_track_customers_who_bought")}
            checked={!clickedAnyMoment.id}
            handler={() => {
              handleAnyMomentClick(anyMomentOptions.filter((v) => v.id !== clickedAnyMoment.id)[0]);
            }}
            disabled={clickedCategory.id === 0 && parameters.item.id === 0}
          />
        )}
      </div>
    </div>)
  );
};

function reducer(state, action) {
  switch (action.type) {
    case 'FOCUS':
      return { ...state, isFocused: true, inputValue: '' };
    case 'UNFOCUS':
      return {
        ...state,
        isFocused: false,
        isSearchRequestEnabled: false,
        inputValue: action.inputValue,
      };
    case 'TYPE':
      return { ...state, isSearchRequestEnabled: true, inputValue: action.inputValue };
    case 'CLICK_CATEGORY':
      return { ...state, clickedCategory: action.clickedCategory };
    case 'CLICK_ITEM_ALL':
      return { ...state, isSearchRequestEnabled: false, clickedCategory: action.clickedCategory };
    case 'CLICK_ANY_MOMENT':
      return { ...state, clickedAnyMoment: action.clickedAnyMoment };
    case 'SELECT_VALUE':
      return {
        ...state,
        isFocused: false,
        isSearchRequestEnabled: false,
        inputValue: action.inputValue,
      };
    case 'REMOVE_VALUE':
      return { ...state, inputValue: '' };
    case 'SEARCH':
      return { ...state, searchedValues: action.searchedValues };
    default:
      return state;
  }
}
const initialState = {
  isFocused: false,
  isSearchRequestEnabled: false,
  clickedCategory: {},
  clickedAnyMoment: {},
  inputValue: '',
  searchedValues: [],
};

const PurchasedItemSearchInput = ({
  label,
  width = 300,
  style = {},
  queryKey,
  anyMomentOptions,
  parameters,
  setParameter,
  setParametersState,
  reportsParameters,
}) => {
  const { userInfo } = useUserInfoContext();
  const containerEl = useRef(null);
  const inputEl = useRef(null);
  const [isClickedInside, setIsClickedInside] = useClickOutside(containerEl);

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
  });

  const { data: searchedOptions, refetch: searchRefetch } = useQuery(
    ['option-search'],
    async () => {
      const { data } = await axiosPrivate.post(customerAPI.OPTION_SEARCH, {
        company_id: userInfo?.company.companyId,
        shop_id: userInfo?.company.shopId,
        parameter: 'category',
        term: inputEl.current.value,
      });
      return data;
    },
    {
      enabled: state.isSearchRequestEnabled,
    }
  );

  const handleClickCategory = (option) => {
    dispatch({
      type: 'CLICK_CATEGORY',
      clickedCategory: option,
    });
  };
  const handleClickAnyMoment = (option) => {
    dispatch({
      type: 'CLICK_ANY_MOMENT',
      clickedAnyMoment: option,
    });
  };
  const handleClickItemValueAll = (category) => {
    dispatch({
      type: 'CLICK_ITEM_ALL',
      clickedCategory: category,
    });
  };
  const handleSelectValue = (category, item, anyMoment) => {
    dispatch({
      type: 'SELECT_VALUE',
      inputValue: `${category.value} > ${item.value}`,
    });

    setParameter(category, 'category');
    setParameter(item, 'item');
    if (category.id === 0 && item.id === 0) {
      dispatch({
        type: 'CLICK_ANY_MOMENT',
        clickedAnyMoment: anyMomentOptions.filter((v) => v.id === false)[0],
      });
      setParameter(anyMomentOptions.filter((v) => v.id === false)[0], 'anyMoment');
    } else {
      setParameter(anyMoment, 'anyMoment');
    }
  };

  useEffect(() => {
    if (parameters.category.value && parameters.item.value) {
      dispatch({
        type: 'SELECT_VALUE',
        inputValue: `${parameters.category.value} > ${parameters.item.value}`,
      });
      dispatch({
        type: 'CLICK_CATEGORY',
        clickedCategory: parameters.category,
      });
      dispatch({
        type: 'CLICK_ANY_MOMENT',
        clickedAnyMoment: parameters.anyMoment,
      });
    }
  }, [parameters]);

  useEffect(() => {
    if (searchedOptions) {
      if (searchedOptions.length === 0) {
        dispatch({
          type: 'SEARCH',
          searchedValues: [],
        });
      } else {
        dispatch({
          type: 'SEARCH',
          searchedValues: searchedOptions.flatMap((category) =>
            category.products.map((item) => ({
              category: {
                id: +category.id,
                value: category.name,
              },
              item: {
                id: +item.id,
                value: item.name,
              },
            }))
          ),
        });
      }
    }
  }, [searchedOptions]);

  useEffect(() => {
    if (state.isFocused && !isClickedInside) {
      dispatch({
        type: 'UNFOCUS',
        inputValue: `${parameters.category.value} > ${parameters.item.value}`,
      });
      dispatch({
        type: 'CLICK_CATEGORY',
        clickedCategory: parameters.category,
      });
      if (parameters.category.id === 0 && parameters.item.id === 0) {
        dispatch({
          type: 'CLICK_ANY_MOMENT',
          clickedAnyMoment: anyMomentOptions.filter((v) => v.id === false)[0],
        });
        setParameter(anyMomentOptions.filter((v) => v.id === false)[0], 'anyMoment');
      } else {
        setParameter(state.clickedAnyMoment, 'anyMoment');
      }
    }
  }, [isClickedInside]);

  return (
    <div className="purchased-item">
      <div
        ref={containerEl}
        style={{ ...style, width: `${width}px`, minWidth: `${width}px` }}
        className="purchased-item_search-input"
      >
        <label className="purchased-item_input-label" onClick={() => setIsClickedInside(false)}>
          {label}
        </label>
        <div className="purchased-item_input-wrapper">
          <input
            ref={inputEl}
            value={state.inputValue}
            placeholder="Search"
            className="purchased-item_input"
            onClick={() => {
              dispatch({
                type: 'FOCUS',
              });
            }}
            onInput={(e) => {
              dispatch({
                type: 'TYPE',
                inputValue: e.target.value,
              });
              debounce(() => {
                searchRefetch();
              }, 500)();
            }}
          />
          {state.isFocused && (
            <img
              src={REMOVETEXT}
              alt="remove-text"
              className="remove-text-icon"
              onClick={() => {
                dispatch({ type: 'REMOVE_VALUE' });
                debounce(() => {
                  searchRefetch();
                }, 500)();
              }}
            />
          )}
          <img
            src={CHEVRON_DOWN}
            alt="drop-down"
            className={`chevron-down${state.isFocused ? ' reverse' : ''}`}
            onClick={() => {
              if (state.isFocused) {
                dispatch({
                  type: 'UNFOCUS',
                });
              } else {
                dispatch({
                  type: 'FOCUS',
                });
                inputEl.current.focus();
              }
            }}
          />
        </div>
        <OptionsDropdown
          isFocused={state.isFocused}
          isSearchRequestEnabled={state.isSearchRequestEnabled}
          queryKey={queryKey}
          parameters={parameters}
          setParametersState={setParametersState}
          reportsParameters={reportsParameters}
          clickedCategory={state.clickedCategory}
          clickedAnyMoment={state.clickedAnyMoment}
          handleCategoryClick={handleClickCategory}
          handleAnyMomentClick={handleClickAnyMoment}
          handleClickItemValueAll={handleClickItemValueAll}
          handleSelectValue={handleSelectValue}
          anyMomentOptions={anyMomentOptions}
          searchedValues={state.searchedValues}
          inputValue={state.inputValue}
        />
      </div>
    </div>
  );
};
export default PurchasedItemSearchInput;
